/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is an item, state or error code; it renders nothing */
// C6 Offline-ready state for the web: one state per item (decks, deck card
// data, collection, binders, card database, scan database, images), read
// from the owner's stored C6 meta and the offline card data module.
//
// Web specifics: scan_database is not applicable (the scanner is native);
// images (W6) are not applicable while VITE_OFFLINE_IMAGES is off or for a guest.
import { useEffect, useState } from 'react'
import { META_COLLECTION, META_DECKS, META_DECK_CARDS, READY_ERROR, READY_ITEM, READY_STATE, STALE_AFTER_MS, type ReadyItem, type ReadyStateKind } from './offline.codes'
import { isAccountOwner, ownerSubject } from './identityMachine'
import { getIdentity, subscribeIdentity } from './identity'
import { readCollectionSnapshot, readDeckSnapshot, readItemStatus, isStale } from './snapshots'
import { getOfflineAdapter, offlineRuntimeActive, onSnapshotsChanged } from './runtime'
import { getOfflineCardsStatus, subscribeOfflineCards } from '../offlineCards/client'
import { STATE as CARDS_STATE } from '../offlineCards/offlineCards.codes'
import { OFFLINE_IMAGES } from '../flags'
import { readOwnerImageStatus, subscribeImageDownload } from './images'

export interface ReadyItemState {
  item: ReadyItem
  state: ReadyStateKind
  percent?: number
  since?: string | null
  lineCount?: number
  limit?: number
  code?: string
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000

function cardDatabase(): ReadyItemState {
  const s = getOfflineCardsStatus()
  const item = READY_ITEM.cardDatabase
  if (!s.eligible || s.state === CARDS_STATE.notApplicable) return { item, state: READY_STATE.notApplicable }
  switch (s.state) {
    case CARDS_STATE.ready: return { item, state: READY_STATE.ready }
    case CARDS_STATE.downloading: return { item, state: READY_STATE.downloading, percent: s.percent ?? 0 }
    case CARDS_STATE.checking: return { item, state: READY_STATE.downloading, percent: 100 }
    case CARDS_STATE.stale: return { item, state: READY_STATE.stale, since: s.checkedAt ?? s.installed?.installed_at ?? null }
    case CARDS_STATE.error: return { item, state: READY_STATE.error, code: s.error === 'storage_full' ? READY_ERROR.storageFull : READY_ERROR.serverError }
    default: return { item, state: READY_STATE.notStarted }
  }
}

/** Every C6 item for offline_owner (an account); guests see only the public items. */
export async function computeReadiness(now = Date.now()): Promise<ReadyItemState[]> {
  const out: ReadyItemState[] = []
  const owner = getIdentity().offlineOwner
  const account = offlineRuntimeActive() && isAccountOwner(owner)
  if (account) {
    const decksStatus = await readItemStatus(owner, META_DECKS)
    const decks = await readDeckSnapshot(owner)
    if (decksStatus?.error) out.push({ item: READY_ITEM.decks, state: READY_STATE.error, code: decksStatus.error.code })
    else if (!decksStatus?.refreshed_at) out.push({ item: READY_ITEM.decks, state: READY_STATE.notStarted })
    else if (isStale(decksStatus.refreshed_at, now, STALE_AFTER_MS)) out.push({ item: READY_ITEM.decks, state: READY_STATE.stale, since: decksStatus.refreshed_at })
    else out.push({ item: READY_ITEM.decks, state: READY_STATE.ready })

    const cardsStatus = await readItemStatus(owner, META_DECK_CARDS)
    if (!decks || decks.decks.length === 0) out.push({ item: READY_ITEM.deckCardData, state: decks ? READY_STATE.ready : READY_STATE.notStarted })
    else if (!cardsStatus?.refreshed_at) out.push({ item: READY_ITEM.deckCardData, state: READY_STATE.notStarted })
    else if ((cardsStatus.missing ?? 0) > 0 || isStale(cardsStatus.refreshed_at, now, WEEK_MS)) out.push({ item: READY_ITEM.deckCardData, state: READY_STATE.stale, since: cardsStatus.refreshed_at })
    else out.push({ item: READY_ITEM.deckCardData, state: READY_STATE.ready })

    const subject = ownerSubject(owner)
    const collectionId = subject ? getOfflineAdapter()?.collectionIdFor(subject) ?? null : null
    let col: ReadyItemState
    if (!collectionId) col = { item: READY_ITEM.collection, state: READY_STATE.notApplicable }
    else {
      const snap = await readCollectionSnapshot(owner, collectionId)
      const st = await readItemStatus(owner, META_COLLECTION)
      if (snap?.too_large) col = { item: READY_ITEM.collection, state: READY_STATE.tooLarge, lineCount: snap.too_large.line_count, limit: snap.too_large.limit }
      else if (st?.error && !snap?.body) col = { item: READY_ITEM.collection, state: READY_STATE.error, code: st.error.code }
      else if (!snap?.body) col = { item: READY_ITEM.collection, state: READY_STATE.notStarted }
      else if (isStale(snap.checked_at, now, STALE_AFTER_MS)) col = { item: READY_ITEM.collection, state: READY_STATE.stale, since: snap.checked_at }
      else col = { item: READY_ITEM.collection, state: READY_STATE.ready }
    }
    out.push(col)
    out.push({ ...col, item: READY_ITEM.binders })
    if (OFFLINE_IMAGES) out.push(await imagesItem(owner))
  }
  out.push(cardDatabase())
  out.push({ item: READY_ITEM.scanDatabase, state: READY_STATE.notApplicable })
  if (!(account && OFFLINE_IMAGES)) out.push({ item: READY_ITEM.images, state: READY_STATE.notApplicable })
  return out
}

/** C6 `images`: ready when every pinned image is stored. */
async function imagesItem(owner: string): Promise<ReadyItemState> {
  const item = READY_ITEM.images
  const s = await readOwnerImageStatus(owner).catch(() => null)
  if (!s) return { item, state: READY_STATE.notStarted }
  switch (s.c6.state) {
    case 'ready': return { item, state: READY_STATE.ready }
    case 'downloading': return { item, state: READY_STATE.downloading, percent: s.c6.percent }
    case 'error': return { item, state: READY_STATE.error, code: s.c6.code === 'storage_full' ? READY_ERROR.storageFull : READY_ERROR.serverError }
    default: return { item, state: READY_STATE.notStarted }
  }
}

/** All green: every applicable item is ready (C6 inv. 1). */
export function allReady(items: ReadyItemState[]): boolean {
  return items.every(i => i.state === READY_STATE.ready || i.state === READY_STATE.notApplicable)
}

export function useOfflineReadiness(): ReadyItemState[] | null {
  const [items, setItems] = useState<ReadyItemState[] | null>(null)
  useEffect(() => {
    let alive = true
    const load = () => { void computeReadiness().then(r => { if (alive) setItems(r) }, () => {}) }
    load()
    const offs = [onSnapshotsChanged(load), subscribeIdentity(load), subscribeOfflineCards(load), ...(OFFLINE_IMAGES ? [subscribeImageDownload(load)] : [])]
    return () => {
      alive = false
      for (const o of offs) o()
    }
  }, [])
  return items
}
